import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const DESKTOP = window.matchMedia('(min-width: 1200px)');

/**
 * <mrk-header>: burger + full-screen menu, live cart count, cart-drawer aria-expanded sync, and the
 * --header-height / --header-group-height variables Horizon features read (its own header is not rendered).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} menu
 * @property {HTMLButtonElement} menuToggle
 * @property {HTMLElement} cartCount
 * @property {HTMLElement} cartCountText
 * @property {HTMLButtonElement} [cartTrigger]
 *
 * @extends {Component<Refs>}
 */
class MrkHeader extends Component {
  requiredRefs = ['menu', 'menuToggle', 'cartCount', 'cartCountText'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.refs.menu.addEventListener('close', this.#onMenuClose);
    this.refs.menu.addEventListener('keydown', this.#trapFocus);
    DESKTOP.addEventListener('change', this.#onBreakpoint);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerChange);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#resizeObserver = new ResizeObserver(this.#setHeights);
    const group = document.getElementById('header-group');
    for (const element of group ? group.querySelectorAll(':scope > .shopify-section') : [this]) {
      this.#resizeObserver.observe(element);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.menu?.removeEventListener('close', this.#onMenuClose);
    this.refs.menu?.removeEventListener('keydown', this.#trapFocus);
    DESKTOP.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerChange);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#resizeObserver?.disconnect();
    document.documentElement.classList.remove('mrk-scroll-lock');
  }

  openMenu() {
    const { menu, menuToggle } = this.refs;
    if (menu.open) return;
    menu.showModal();
    menuToggle.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('mrk-scroll-lock');
  }

  closeMenu() {
    if (this.refs.menu.open) this.refs.menu.close();
  }

  #onMenuClose = () => {
    this.refs.menuToggle.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('mrk-scroll-lock');
    if (!DESKTOP.matches) this.refs.menuToggle.focus();
  };

  /**
   * Keeps Tab / Shift+Tab cycling inside the open menu (a modal dialog alone lets focus escape to the browser UI).
   * @param {KeyboardEvent} event
   */
  #trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = /** @type {HTMLElement[]} */ ([...this.refs.menu.querySelectorAll('a[href], button:not([disabled])')]);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {MediaQueryListEvent} event */
  #onBreakpoint = (event) => {
    if (event.matches) this.closeMenu();
  };

  /** @param {Event} event */
  #onDrawerChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer' || !this.refs.cartTrigger) return;
    this.refs.cartTrigger.setAttribute('aria-expanded', target.hasAttribute('open') ? 'true' : 'false');
  };

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number') this.#renderCount(count);
        else this.#refreshCount();
      })
      .catch(() => this.#refreshCount());
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshCount();
  };

  async #refreshCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.#renderCount(cart.item_count);
    } catch {
      /* Network hiccup: keep the server-rendered count. */
    }
  }

  /** @param {number} count */
  #renderCount(count) {
    const { cartCount, cartCountText } = this.refs;
    cartCount.textContent = count < 100 ? String(count) : '99+';
    cartCount.classList.toggle('mrk-header__count--empty', count === 0);
    const template = count === 1 ? this.dataset.mrkCountOne : this.dataset.mrkCountOther;
    if (template) cartCountText.textContent = template.replace('__n__', String(count));
  }

  #setHeights = () => {
    const group = document.getElementById('header-group');
    let groupHeight = 0;
    for (const element of group ? group.children : []) {
      if (element instanceof HTMLElement) groupHeight += element.offsetHeight;
    }
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || this.offsetHeight}px`);
  };
}

if (!customElements.get('mrk-header')) customElements.define('mrk-header', MrkHeader);
